import { isEmpty } from "lodash-es";
import { useFormContext, useWatch } from "react-hook-form";
import { useSupabaseLoader } from "../../../server/supabase/hooks";
import type { UseSupabaseLoaderState } from "../../../server/supabase/hooks/types";
import type { UUID } from "../../../utils/uuid";
import { SupabaseMomentPreviewService } from "../../moment/server/supabase-preview-service";
import type { MomentFormMoment } from "../../moment/types/moment-form-moment";

export function useDynamicSegmentCounts(): UseSupabaseLoaderState<
  Record<string, { name: string; count: number }>
> {
  const { control, getValues } = useFormContext<MomentFormMoment>();
  useWatch({
    control,
    name: [
      "dynamic_segment_id_map",
      "target_manager",
      "target_connection_type",
      "segment.segment_json",
    ],
  });
  const [dynamic_segment_id_map, target_manager, target_connection_type, segment_json] = getValues([
    "dynamic_segment_id_map",
    "target_manager",
    "target_connection_type",
    "segment.segment_json",
  ]);

  return useSupabaseLoader(
    async ({ supabase, account_id }) => {
      if (isEmpty(dynamic_segment_id_map)) return {};

      const service = new SupabaseMomentPreviewService(supabase);

      const { data, error } = await service.getDynamicSegmentCountsSegmentJson(
        segment_json,
        account_id,
        Object.keys(dynamic_segment_id_map) as UUID[],
        target_manager,
        target_connection_type ?? undefined,
      );

      if (error) {
        console.error("Error fetching dynamic segment counts", error);
        throw error;
      }

      return (
        data?.reduce(
          (acc, { id, name, count }) => {
            acc[id] = { name, count };
            return acc;
          },
          {} as Record<string, { name: string; count: number }>,
        ) ?? {}
      );
    },
    [dynamic_segment_id_map, target_manager, target_connection_type, segment_json],
  );
}
